/**
 * ServerWakeUpBanner - 서버 절전 모드 해제 상태 배너
 *
 * Fly.io auto-stop으로 서버가 절전 모드일 때
 * 사용자에게 상태를 안내하는 배너
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Server, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import { useServerWakeUp } from '../../hooks/useServerWakeUp';

export const ServerWakeUpBanner: React.FC = () => {
  const { status, message, isWaking, isError, retry } = useServerWakeUp();

  // idle 상태면 렌더링하지 않음
  if (status === 'idle') return null;

  const isVisible = isWaking || isError || status === 'ready';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6 overflow-hidden"
        >
          <div
            className={`
              p-4 rounded-xl border backdrop-blur-sm
              ${
                isWaking
                  ? 'border-emerald-400/30 bg-emerald-400/10'
                  : isError
                    ? 'border-[var(--danger)]/30 bg-[var(--danger)]/10'
                    : 'border-[var(--success)]/30 bg-[var(--success)]/10'
              }
            `}
          >
            <div className="flex items-center gap-3">
              {/* 아이콘 */}
              {isWaking ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                  className="flex-shrink-0"
                >
                  <Server size={20} className="text-emerald-400" />
                </motion.div>
              ) : isError ? (
                <AlertTriangle
                  size={20}
                  className="text-[var(--danger)] flex-shrink-0"
                />
              ) : (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--success)] flex-shrink-0"
                  />
                </motion.div>
              )}

              {/* 메시지 */}
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium ${
                    isWaking
                      ? 'text-emerald-400'
                      : isError
                        ? 'text-[var(--danger)]'
                        : 'text-[var(--success)]'
                  }`}
                >
                  {message}
                </p>
                {isWaking && (
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">
                    아직 사용자가 많지 않아 무료 서버를 쓰고 있습니다. 양해 부탁드려요!
                  </p>
                )}
                {isError && (
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">
                    서버가 너무 깊이 잠들었나 봐요... 아래 버튼으로 다시 깨워주세요!
                  </p>
                )}
              </div>

              {/* 재시도 버튼 */}
              {isError && (
                <motion.button
                  onClick={retry}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title="다시 시도"
                >
                  <RefreshCw size={16} className="text-[var(--danger)]" />
                </motion.button>
              )}
            </div>

            {/* 진행 바 (waking 상태일 때만) */}
            {isWaking && (
              <div className="mt-3 h-1 bg-[rgba(var(--surface-rgb),0.5)] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-emerald-400/60 rounded-full"
                  initial={{ width: '0%' }}
                  animate={{ width: ['0%', '100%'] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut',
                  }}
                />
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ServerWakeUpBanner;
